import { UseCartApi } from '@/services/m-commerce-api/cart'
import { AsyncReducer } from '@/types'
import { CartItem } from '@/types/data-types'

import { CartState, CartActions } from './types'
import { getEmptyCart } from './local-reducer'
import { isNil } from '@/utils/validations'
import { CartRes, MergeCartReq } from '@/services/m-commerce-api/cart/types'

const addItemToCart = async (_state: CartState, { cartApi, item, isdn }: { cartApi: ReturnType<UseCartApi>, item: CartItem, isdn: string }): Promise<CartState> => {
  if (isNil(item.price)) {
    throw new Error('Invalid product. Price not found')
  }

  const res = await cartApi.addToCart(isdn, {
    productId: item.productId,
    quantity: item.quantity,
    price: item.price,
    nameMn: item.nameMn,
    nameEn: item.nameEn,
    image: item.image,
    payload: item.payload
  })

  return responseHandler(res)
}

const removeItemFromCart = async (state: CartState, { cartApi, isdn, productId }: { cartApi: ReturnType<UseCartApi>, isdn: string, productId: number }): Promise<CartState> => {
  return await updateCartItem(state, { cartApi, productId, quantity: 0, isdn })
}

const updateCartItem = async (state: CartState, { cartApi, isdn, productId, quantity }: { cartApi: ReturnType<UseCartApi>, isdn: string, productId: number, quantity: number }): Promise<CartState> => {
  const existingCartItemIndex = state.items.findIndex(
    (i) => i.productId === productId
  )
  if (existingCartItemIndex > -1) {
    const res = await cartApi.modifyCartItem(isdn, {
      productId,
      quantity
    })

    return responseHandler(res)
  } else {
    throw new Error('Product not found in cart')
  }
}

const clearCart = async (_state: CartState, { isdn, cartApi }: { isdn: string, cartApi: ReturnType<UseCartApi> }): Promise<CartState> => {
  const res = await cartApi.clearCart(isdn) ?? getEmptyCart()
  return responseHandler(res)
}

const fetchCart = async (_state: CartState, { isdn, cartApi }: { isdn: string, cartApi: ReturnType<UseCartApi> }): Promise<CartState> => {
  const res = await cartApi.getCart(isdn) ?? getEmptyCart()
  return responseHandler(res)
}

const mergeCart = async (_state: CartState, { isdn, req, cartApi }: { isdn: string, cartApi: ReturnType<UseCartApi>, req: MergeCartReq }): Promise<CartState> => {
  const res = await cartApi.mergeCart(isdn, req)
  return responseHandler(res)
}

const responseHandler = (cartRes: CartRes): CartState => {
  const { amountDetails = [], totalQty = 0, cartData = [] } = cartRes ?? {}
  const [total] = amountDetails.filter((a) => a.key === 'total')

  const { amount = '0' } = total

  const totalAmount = parseFloat(amount as string)
  const subTotal = parseFloat((totalAmount / 1.11).toFixed(2))
  const vat = totalAmount - subTotal

  const cartState: CartState = {
    items: cartData,
    summary: {
      amountDetails: [{
        amount: subTotal,
        key: 'subTotal'
      },
      {
        amount: vat,
        key: 'vat'
      }],
      quantity: totalQty,
      total: Number(total?.amount ?? 0)
    }
  }

  return cartState
}

export const reducer: AsyncReducer<CartState, CartActions> = async (state, action) => {
  switch (action.type) {
    case CartActions.ADD_ITEM:
      return await addItemToCart(state, action.payload)
    case CartActions.REMOVE_ITEM:
      return await removeItemFromCart(state, action.payload)
    case CartActions.UPDATE_ITEM:
      return await updateCartItem(state, action.payload)
    case CartActions.CLEAR_CART:
      return await clearCart(state, action.payload)
    case CartActions.FETCH_CART:
      return await fetchCart(state, action.payload)
    case CartActions.MERGE_CART:
      return await mergeCart(state, action.payload)
  }
}
